import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../utils/axios";

// Static class maps — Tailwind's JIT can't see runtime-interpolated class names,
// so accent styles must be full literal strings per variant.
const ACCENTS = {
  purple: {
    wrap: "border-purple-200 bg-purple-50/60",
    icon: "text-purple-500",
    label: "text-purple-700",
    ring: "focus:ring-purple-500",
    btn: "from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700",
  },
  amber: {
    wrap: "border-amber-200 bg-amber-50/60",
    icon: "text-amber-500",
    label: "text-amber-700",
    ring: "focus:ring-purple-500",
    btn: "from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700",
  },
  green: {
    wrap: "border-green-200 bg-green-50/60",
    icon: "text-green-500",
    label: "text-green-700",
    ring: "focus:ring-purple-500",
    btn: "from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700",
  },
};

/**
 * Small "type keywords → AI fills the fields" helper.
 *
 * Calls POST /api/vendor/ai/service-assist with the given `step`, passing the
 * vendor's keywords as `prompt` and the current form as `formData`. The parsed
 * `data.data` (already the step's JSON result) is handed back via `onResult`
 * so the parent decides how to apply it to its own form state.
 *
 * Props:
 *  - serviceType   e.g. "venue" | "caterers"
 *  - step          "areas" | "cuisine" | ...
 *  - formData      current form object (for context)
 *  - onResult(res) called with the AI result object
 *  - placeholder   input placeholder
 *  - label         heading text
 *  - accent        tailwind color base, e.g. "amber" | "green" (default "purple")
 */
export default function AIKeywordFill({
  serviceType,
  step,
  formData = {},
  onResult,
  placeholder = "Type keywords…",
  label = "Fill with AI",
  buttonText = "Generate",
  accent = "purple",
}) {
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!keywords.trim()) return toast.error("Type a few keywords first");
    setLoading(true);
    try {
      const { data } = await api.post("/api/vendor/ai/service-assist", {
        serviceType,
        step,
        formData,
        prompt: keywords.trim(),
      });
      onResult?.(data?.data || {});
      toast.success("AI applied to the form");
    } catch (e) {
      toast.error(e.response?.data?.message || "AI could not generate — try different keywords");
    } finally {
      setLoading(false);
    }
  };

  const c = ACCENTS[accent] || ACCENTS.purple;

  return (
    <div className={`rounded-xl border p-3 mb-4 ${c.wrap}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className={`w-3.5 h-3.5 ${c.icon}`} />
        <span className={`text-xs font-semibold ${c.label}`}>{label}</span>
        <span className="text-[10px] text-gray-400">— type keywords, AI fills it in</span>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              run();
            }
          }}
          placeholder={placeholder}
          className={`flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:ring-2 ${c.ring}`}
        />
        <button
          type="button"
          onClick={run}
          disabled={loading}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r ${c.btn} text-white text-sm font-semibold transition-all disabled:opacity-60 whitespace-nowrap`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "…" : buttonText}
        </button>
      </div>
    </div>
  );
}
